import React from 'react';
import { ShoppingCart, ArrowRight, Star, TrendingUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useCart } from '@/contexts/CartContext';

const FEATURED_SERVICES = [
  {
    id: 'pack-express',
    name: 'Pack Express Relocalización',
    description: 'Orientación inicial y documentación básica para empezar tu mudanza a Suiza.',
    price: 199,
    popular: false,
    trending: true,
  },
  {
    id: 'pack-esencial',
    name: 'Pack Esencial Relocalización',
    description: 'Incluye llegada asistida, trámites iniciales y soporte práctico desde el primer día.',
    price: 649,
    popular: true,
    trending: false,
  },
  {
    id: 'pack-completo',
    name: 'Pack Completo Relocalización',
    description: 'Soporte extendido, prioridad en vivienda y acompañamiento integral en el proceso.',
    price: 949,
    popular: false,
    trending: true,
  },
  {
    id: 'copias-cv',
    name: 'Copias CV (50 unidades)',
    description: 'Impresión profesional de currículums para presentaciones laborales en Suiza.',
    price: 25,
    popular: true,
    trending: false,
  },
];

interface CartEmptyStateProps {
  onClose: () => void;
}

export const CartEmptyState: React.FC<CartEmptyStateProps> = ({ onClose }) => {
  const { addItem } = useCart();

  const handleAddService = (product: (typeof FEATURED_SERVICES)[0]) => {
    addItem({
      id: product.id,
      title: product.name,
      price: product.price,
      description: product.description,
    });
  };

  const handleViewAllServices = () => {
    onClose();
    window.location.href = '/servicios';
  };

  return (
    <div className="flex-1 flex flex-col items-center p-6 text-center overflow-y-auto">
      <div className="animate-bounce mb-4 mt-2">
        <ShoppingCart className="h-16 w-16 text-muted-foreground mx-auto" />
      </div>

      <h3 className="text-xl font-semibold mb-2">Tu carrito está vacío</h3>
      <p className="text-muted-foreground mb-8 max-w-sm">
        Escoge entre nuestros packs de relocalización y servicios adicionales para tu mudanza a Suiza
      </p>

      <div className="w-full space-y-4 mb-6">
        <h4 className="text-sm font-medium text-left flex items-center">
          <TrendingUp className="h-4 w-4 mr-2 text-primary" />
          Packs de Relocalización
        </h4>

        {FEATURED_SERVICES.map((product) => (
          <Card key={product.id} className="hover:shadow-md transition-all duration-200 cursor-pointer group">
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1 text-left">
                  <div className="flex items-center space-x-2 mb-1">
                    <h5 className="font-medium text-sm">{product.name}</h5>
                    {product.popular && (
                      <Badge variant="secondary" className="text-xs">
                        <Star className="h-3 w-3 mr-1" />
                        Popular
                      </Badge>
                    )}
                    {product.trending && (
                      <Badge variant="secondary" className="text-xs">
                        <TrendingUp className="h-3 w-3 mr-1" />
                        Destacado
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mb-2 line-clamp-2">{product.description}</p>
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-primary">{product.price} CHF</span>
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-xs h-7 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleAddService(product);
                      }}
                    >
                      Añadir
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Button onClick={handleViewAllServices} className="w-full">
        Ver todos los servicios
        <ArrowRight className="h-4 w-4 ml-2" />
      </Button>
    </div>
  );
};